import { isRestrictedCustomerBrand } from './restrictedBrands';

const MAX_URL_LENGTH = 2048;

// Hat der String bereits ein Schema? ("https:", "javascript:", "mailto:" …)
// Kein Punkt im Schema-Teil: sonst würde "dein-shop.de:8443" als Schema
// "dein-shop.de" gelesen und verworfen.
const SCHEME_RE = /^[a-z][a-z0-9+-]*:/i;

// Hostname NACH dem URL-Parsing (IDN ist dann bereits Punycode, z. B.
// "müller-shop.de" → "xn--mller-shop-9db.de"): mindestens ein Punkt und eine
// alphabetische TLD mit 2+ Zeichen. Schließt "localhost", "dein-shop" und
// nackte IP-Adressen aus.
const HOSTNAME_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i;

/**
 * Normalisiert eine frei eingegebene Website-Adresse auf eine absolute http(s)-URL.
 * Fehlt das Schema, wird `https://` ergänzt. Gibt `null` zurück, wenn die Eingabe
 * kein plausibler Web-Host ist (leer, Whitespace, kein Punkt, fremdes Schema wie
 * `javascript:`/`mailto:`, Userinfo im Host, zu lang).
 *
 * Hintergrund (UX-Review 2026-07, P0-2): "dein-shop.de" — der Placeholder des
 * CTA-Felds auf der Startseite — wurde vorher überall verworfen, weil `new URL()`
 * ohne Schema wirft. Ohne Website entsteht nie eine Marke.
 *
 * ⚠️ SPIEGEL-IMPLEMENTIERUNG: `supabase/functions/_shared/websiteUrl.ts`.
 * Deno-Function und Frontend teilen keinen Code — beide Dateien sind bewusst
 * duplizierte, aber identische Logik. Änderungen IMMER an beiden Stellen
 * vornehmen (Tests: `tests/unit/websiteUrlNormalizer.test.ts`).
 */
export function normalizeWebsiteUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;
  // Interner Whitespace ist in einer URL nie gültig (Copy-Paste aus Fließtext).
  if (/\s/.test(trimmed)) return null;
  if (trimmed.length > MAX_URL_LENGTH) return null;

  let withScheme: string;
  if (SCHEME_RE.test(trimmed)) {
    const scheme = trimmed.slice(0, trimmed.indexOf(":")).toLowerCase();
    if (scheme !== "http" && scheme !== "https") return null;
    withScheme = trimmed;
  } else {
    // "//dein-shop.de" (protokoll-relativ) und "/dein-shop.de" sauber abfangen.
    withScheme = `https://${trimmed.replace(/^\/+/, "")}`;
  }

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  // "https://user:pass@example.com" — Zugangsdaten in der Marken-URL wollen wir nicht.
  if (url.username || url.password) return null;
  if (!HOSTNAME_RE.test(url.hostname)) return null;

  const normalized = url.toString();
  return normalized.length > MAX_URL_LENGTH ? null : normalized;
}

// Erkennt vertauschte Felder: ein Markenname wie "https://hotty-bbq.shop/" oder
// "hotty-bbq.shop" ist offensichtlich eine Web-Adresse. Bewusst konservativ:
// nur Schema-Prefix ODER ein einzelnes Token ohne Leerzeichen mit Punkt + TLD,
// damit echte Namen wie "Dr. Oetker" oder "Hotty BBQ" nie anschlagen.
const BARE_HOST_TOKEN_RE = /^(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/\S*)?$/i;

/**
 * True, wenn ein (Marken-)Name wie eine Website-Adresse aussieht.
 * Vorfall 2026-08-18: URL im Namensfeld, Name im URL-Feld — die Pipeline lief
 * mit "https://Hotty BBQ" bis "completed" durch, ohne dass es jemand merkte.
 */
export function looksLikeWebsiteAddress(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (/^www\./i.test(trimmed)) return true;
  if (/\s/.test(trimmed)) return false;
  return BARE_HOST_TOKEN_RE.test(trimmed);
}

export type InitialBrandInput = {
  brandName: string;
  companyName: string;
  websiteUrl: string;
};

export type ValidatedInitialBrandInput = {
  brandName: string;
  companyName: string;
  websiteUrl: string;
};

export type InitialBrandValidationError =
  | "brand_name_too_short"
  | "brand_name_looks_like_url"
  | "company_name_too_short"
  | "brand_not_allowed"
  | "website_invalid";

/**
 * Validates the mandatory first step of a new workspace's onboarding.
 * Keeping this independent from the form makes the same rules easy to test.
 */
export function validateInitialBrandInput(
  input: InitialBrandInput,
): { data: ValidatedInitialBrandInput; error: null } | { data: null; error: InitialBrandValidationError } {
  const brandName = input.brandName.trim();
  if (isRestrictedCustomerBrand(input)) {
    return { data: null, error: "brand_not_allowed" };
  }
  if (brandName.length < 2) {
    return { data: null, error: "brand_name_too_short" };
  }
  if (looksLikeWebsiteAddress(brandName)) {
    return { data: null, error: "brand_name_looks_like_url" };
  }

  const companyName = input.companyName.trim();
  if (companyName.length < 2) {
    return { data: null, error: "company_name_too_short" };
  }

  const websiteUrl = normalizeWebsiteUrl(input.websiteUrl);
  if (!websiteUrl) {
    return { data: null, error: "website_invalid" };
  }

  return { data: { brandName, companyName, websiteUrl }, error: null };
}
